import { Button, Form, Modal, Popconfirm, Row, Select } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { PaginationProps } from 'antd/lib/pagination';
import Table, { ColumnProps } from 'antd/lib/table';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IPageResult } from 'src/entity/BaseResult';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { BookKeepingListItem, BookKeepingSearchParam, InstitutionListItem } from 'src/entity/BookKeepingEntity';
import I18N from 'src/services/I18NService';
import BookKeepingService from 'src/services/BookKeepingService';
import BookKeepingSearch from './BookKeepingSearch';
import { UtilDateTime } from 'src/utils/datetime';

const { Option } = Select;

interface IProps extends RouteComponentProps<{}, {}, {useCache: boolean}>, FormComponentProps<{}> {
  isDomestic: boolean,
  isAgent: boolean,
  userInfo: IAgentInfo | IDrugStoreInfo | undefined
}

const BookKeepingListPage: React.FC<IProps> = (props) => {
  const {
    isDomestic,
    isAgent,
    form,
  } = props;
  const { getFieldDecorator, setFieldsValue } = form;
  
  if (!isDomestic || isAgent) {
    // 目前只支持配药中心
    return (
      <Row type="flex" align="top" style={{height: '100%', flexWrap: 'nowrap'}}>
        目前只支持境内配药中心
      </Row>
    )
  }
  // table 列配置
  const columns: Array<ColumnProps<BookKeepingListItem>> = useMemo(() => {
    const result: Array<ColumnProps<BookKeepingListItem>> = [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
        render: (text: any, record: BookKeepingListItem, index: number): React.ReactNode => {
          return `${index + 1}`;
        },
      },
      {
        title: '机构名',
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: '管理员',
        key: 'admin_name',
        dataIndex: 'admin_name',
      },
      {
        title: '手机号',
        key: 'admin_mobile',
        dataIndex: 'admin_mobile',
      },
      {
        title: '机构地址',
        key: 'address',
        dataIndex: 'address',
      },
      {
        title: '添加时间',
        key: 'created_time',
        dataIndex: 'created_time',
        render: (text: any, record: BookKeepingListItem, index: number): React.ReactNode => {
          const formatTime = record.created_time ? UtilDateTime.getDateTimeFormat(record.created_time) : '';
          return formatTime;
        },
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        render: (text: any, record: BookKeepingListItem, index: number): React.ReactNode => {
          return (
            <Popconfirm
              title="删除后将无法使用记账功能"
              onConfirm={() => handleDelete(record)}
              okText={I18N.get('common_ok')}
              cancelText={I18N.get('common_cancel')}
              disabled={loading}
            >
                <a>删除</a>
            </Popconfirm>
          );
        },
      },
    ];
    return result;
  }, []);

  const [loading, setLoading] = useState(false);

  // 初始化查询参数
  const defaultQuery = new BookKeepingSearchParam();
  const [searchQuery, setQuery] = useState(defaultQuery as Partial<BookKeepingSearchParam>);

  // 初始化分页器配置
  const [pagination, setPagination] = useState(() => {
    const {limit, offset} = searchQuery;
    const current = Math.floor((offset as number) / (limit as number)) + 1;
    const pageSize = limit;
    const initialPagination = BookKeepingService.initPagination(current, pageSize);
    return initialPagination;
  });

  // 初始化table列表数组
  const [list, setList] = useState([] as BookKeepingListItem[]);

  const queryRef = useRef<Partial<BookKeepingSearchParam>>();
  const paginationRef = useRef<PaginationProps>();

  // 控制模态窗显示
  const [modalVisible, setModalVisible] = useState(false);

  // 机构查询loading
  const [institutionLoading, setInstitutionLoading] = useState(false);
  // 查询到的机构列表
  const [institutionList, setinstitutionList] = useState([] as InstitutionListItem[]);

  useLayoutEffect(() => {
    queryRef.current = searchQuery;
    paginationRef.current = pagination;
  })


  useEffect(() => {
    // 第一次渲染时 => 查询 用户列表
    doSearch(searchQuery);
  }, []);


  /** 
   * 执行查询
   */
  const doSearch = async (query: Partial<BookKeepingSearchParam>) => {
    setLoading(true);
    let res: IPageResult<BookKeepingListItem[]> | undefined;
    res = await BookKeepingService.fetchBookKeepings(query);
    if (res) {
      const current = Math.floor((query.offset as number) / (query.limit as number)) + 1;
      const newPagination: PaginationProps = {
        ...pagination,
        current,
        pageSize: query.limit,
        total: res.count
      };
      setPagination(newPagination);
      setList(res.results);
    }
    setLoading(false);
  };

  /**
   * 搜索按钮点击回调函数
   */
   const handleSearch = useCallback((query: Partial<BookKeepingSearchParam>) => {
    // 需要获取 pageSize
    const {pageSize} = paginationRef.current as PaginationProps;
    query.offset = 0;
    query.limit = pageSize;
    setQuery(query);
    doSearch(query);
  }, []);

  /**
   * 分页器变化回调函数
   */
   const handlePaginationChange = (paginationTable: PaginationProps) => {
    const {current, pageSize} = paginationTable;
    const param: Partial<BookKeepingSearchParam> = {
      ...queryRef.current as Partial<BookKeepingSearchParam>,
      offset: ((current || 1) - 1) * (pageSize as number),
      limit: pageSize as number,
    }
    setQuery(param);
    doSearch(param);
  };

  /**
   * 删除已关联的机构
   * @param record 
   */
  const handleDelete = async (record: BookKeepingListItem) => {
    const { uuid } = record;
    const isSuccessed = await BookKeepingService.deleteBookKeepingInstitution(uuid);
    if (isSuccessed) {
      doSearch(searchQuery);
    }
  };

  /**
   * 开启新增关联机构弹窗
   */
  const openModal = () => {
    setModalVisible(true);
    setinstitutionList([]);
  }

  /**
   * 关闭模态窗
   * @param record 
   */
  const closeModal = () => {
    setModalVisible(false);
  }

  /**
   * 添加记账机构
   */
  const addInstitution = () => {
    form.validateFields(async (err, values: {uuid: string}) => {
      if (!err) {
        const {
          uuid,
        } = values;
        const matchedItem = institutionList.find(item => item.uuid === uuid);
        if (!!matchedItem) {
          const {
            uuid: institution_uuid,
            name,
            admin_name,
            admin_mobile,
            address,
          } = matchedItem;
          const params: Partial<BookKeepingListItem> = {
            institution_uuid,
            name,
            address,
            admin_name,
            admin_mobile,
          };
          setLoading(true);
          const isSuccessed = await BookKeepingService.addBookKeepingInstitution(params);
          setLoading(false);
          if (isSuccessed) {
            closeModal();
            doSearch(searchQuery);
          }
        }
      }
    })
  }

  /**
   * 根据手机号查询机构
   */
  const searchInstitution = async(value: string) => {
    const reg = /^[0-9]{11}$/gi;
    if (!institutionLoading) {
      setFieldsValue({uuid: undefined});
      if (reg.test(value)) {
        setInstitutionLoading(true);
        const res = await BookKeepingService.fetchInstitutions({mobile: value});
        setinstitutionList(res);
        setInstitutionLoading(false);
      } else {
        setinstitutionList([]);
      }
    }
  }

  const handelInstitutionChange = (value: string | undefined) => {
    if (!value) {
      setinstitutionList([]);
    }
  }


  return (
    <div>
      <BookKeepingSearch
          defaultQuery={searchQuery}
          loading={loading}
          onSearch={handleSearch}/>
      <Row type="flex" justify="end">
        <Button type="primary" onClick={openModal}>
          新增机构
        </Button>
      </Row>
      <Table
        style={{marginTop: 24}}
        loading={loading}
        columns={columns}
        rowKey='uuid'
        pagination={pagination}
        dataSource={list}
        onChange={handlePaginationChange}
      />
      <Modal
        centered={true}
        title="新增记账机构"
        visible={modalVisible}
        destroyOnClose={true}
        footer={null}
        onCancel={closeModal}
        width={480}
      >
        <div style={{ marginBottom: 15, height: 60, color: '#222' }}>
          <Form>
            <Form.Item
              label={'管理员手机'}
              style={{ marginBottom: 0 }}
              labelAlign="left"
              labelCol={{span: 6}}
              wrapperCol={{span: 18}}
            >
              {getFieldDecorator('uuid', {
                rules: [
                  {
                    required: true,
                    message: '请输入机构管理员手机号',
                  },
                ],
                initialValue: undefined,
              })(
                <Select
                  showSearch={true}
                  placeholder="请输入完整的机构管理员手机号"
                  defaultActiveFirstOption={false}
                  filterOption={false}
                  allowClear={true}
                  onSearch={searchInstitution}
                  onChange={handelInstitutionChange}
                >
                  {institutionList.map(item => (
                    <Option key={item.uuid} value={item.uuid}>{item.search_display}</Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Form>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button type="primary" loading={loading} onClick={addInstitution}>
            {I18N.get('common_ok')}
          </Button>
          <Button style={{ marginLeft: 20 }} type="default" onClick={closeModal}>
            {I18N.get('common_cancel')}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
const WrappedBookKeepingListComp = Form.create<IProps>()(BookKeepingListPage);
export default WrappedBookKeepingListComp;



// WEBPACK FOOTER //
// ./src/components/views/BookKeeping/BookKeepingListPage.tsx